import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Eye,
  ShieldHalf,
  Workflow,
} from 'lucide-react';
import { useData, useUI } from '@/data/stores';
import {
  Badge,
  Button,
  Card,
  KeyValueGrid,
  MetricCard,
} from '@/components';
import { MITRE_ICS_TACTICS } from '@/domain';
import { fmtRelative, fmtUTC } from '@/lib/format';
import { severityColor, severityLabel } from '@/lib/color';
import { demoTimestamp, downloadJson } from '@/lib/export';
import './alerts.css';

export function AlertDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const alerts = useData((s) => s.alerts);
  const assets = useData((s) => s.assets);
  const actors = useData((s) => s.actors);
  const acknowledge = useData((s) => s.acknowledgeAlert);
  const containAlert = useData((s) => s.containAlert);
  const notify = useUI((s) => s.notifyAction);

  const alert = alerts.find((a) => a.alert_id === id);
  if (!alert) {
    return (
      <div className="iris-page">
        <h1>Alert not found</h1>
        <Button onClick={() => navigate('/alerts')}>Back to alerts</Button>
      </div>
    );
  }
  const linkedAssets = alert.asset_ids
    .map((aid) => assets.find((x) => x.asset_id === aid))
    .filter((x): x is NonNullable<typeof x> => Boolean(x));
  const actor = actors.find((a) => alert.threat_actor?.includes(a.actor_id));

  return (
    <div className="iris-page">
      <header className="iris-page-header iris-detail-header">
        <div>
          <button className="iris-back" onClick={() => navigate('/alerts')}>
            <ArrowLeft size={14} strokeWidth={1.5} /> All alerts
          </button>
          <div className="iris-alert-eyebrow">
            <span
              className="iris-alert-sev"
              style={{ color: severityColor(alert.severity) }}
            >
              ● {severityLabel(alert.severity)}
            </span>
            <span className="caption mono">{alert.alert_id}</span>
            <span className="caption">·</span>
            <span className="caption">{fmtRelative(alert.detected_at)}</span>
          </div>
          <h1 style={{ marginTop: 4 }}>{alert.title}</h1>
        </div>
        <div className="iris-page-actions">
          <Button
            icon={<Eye size={14} strokeWidth={1.5} />}
            onClick={() => {
              downloadJson(`iris-pcap-manifest-${alert.alert_id}-${demoTimestamp()}.json`, {
                alert_id: alert.alert_id,
                pcap_refs: alert.evidence.filter((e) => e.kind === 'pcap'),
                evidence: alert.evidence,
                chain_of_custody: {
                  exported_at: new Date().toISOString(),
                  classification: 'official',
                  demo_manifest: true,
                },
              });
              notify({
                kind: 'success',
                title: 'PCAP manifest exported',
                body: 'For the hosted demo this downloads the forensic evidence manifest instead of a binary capture.',
              });
            }}
          >
            Open PCAP
          </Button>
          <Button
            icon={<CheckCircle2 size={14} strokeWidth={1.5} />}
            onClick={() => {
              acknowledge(alert.alert_id);
              notify({
                kind: 'success',
                title: 'Alert acknowledged',
                body: `${alert.alert_id} moved into investigation workflow.`,
              });
            }}
          >
            Acknowledge
          </Button>
          <Button
            variant="primary"
            icon={<ShieldHalf size={14} strokeWidth={1.5} />}
            onClick={() => {
              containAlert(alert.alert_id);
              notify({
                kind: 'warning',
                title: 'Alert contained',
                body: alert.enforcement_action
                  ? `Existing enforcement ${alert.enforcement_action} preserved; affected assets moved into containment workflow.`
                  : 'Quarantine enforcement recorded for affected assets in the demo incident workflow.',
              });
            }}
          >
            Quarantine
          </Button>
        </div>
      </header>

      <section className="iris-page-metrics">
        <MetricCard label="Severity" value={severityLabel(alert.severity)} status={severityColor(alert.severity)} />
        <MetricCard label="Status" value={alert.status} />
        <MetricCard label="Category" value={alert.category.replace(/_/g, ' ')} />
        <MetricCard
          label="Confidence"
          value={`${alert.confidence}%`}
          caption="composite"
          status={
            alert.confidence >= 80
              ? 'var(--status-operational)'
              : alert.confidence >= 60
                ? 'var(--status-degraded)'
                : 'var(--status-critical)'
          }
        />
        <MetricCard label="Affected assets" value={linkedAssets.length} />
        <MetricCard label="Enforcement" value={alert.enforcement_action ?? '-'} />
      </section>

      <div className="iris-detail-grid">
        <Card title="Description">
          <p>{alert.description}</p>
        </Card>

        <Card title="Network context">
          <KeyValueGrid
            cols={2}
            items={[
              { label: 'Source IP', value: alert.source_ip ?? '-', mono: true },
              { label: 'Destination IP', value: alert.destination_ip ?? '-', mono: true },
              { label: 'Protocol', value: alert.protocol ?? '-', mono: true },
              { label: 'Zone', value: alert.zone ?? '-', mono: true },
              { label: 'Detected at (UTC)', value: fmtUTC(alert.detected_at), mono: true },
              {
                label: 'Acknowledged',
                value: alert.acknowledged_at ? fmtUTC(alert.acknowledged_at) : '-',
                mono: true,
              },
            ]}
          />
        </Card>

        <Card title="Linked assets">
          <ul className="iris-alert-asset-list">
            {linkedAssets.map((a) => (
              <li
                key={a.asset_id}
                onClick={() => navigate(`/assets/${a.asset_id}`)}
                className="iris-alert-asset"
              >
                <div>
                  <div>{a.name}</div>
                  <div className="caption mono">
                    {a.asset_id} · {a.country} · {a.type.replace(/_/g, ' ')}
                  </div>
                </div>
                <Badge color="var(--status-info)">{a.sector.replace(/_/g, ' ')}</Badge>
              </li>
            ))}
          </ul>
        </Card>

        {alert.mitre && alert.mitre.length > 0 && (
          <Card title="MITRE ATT&CK for ICS">
            <div className="iris-mitre-grid">
              {MITRE_ICS_TACTICS.map((t) => {
                const matched = alert.mitre!.filter((m) => m.tactic_id === t.tactic_id);
                return (
                  <div
                    key={t.tactic_id}
                    className={`iris-mitre-tactic ${matched.length > 0 ? 'active' : ''}`}
                  >
                    <div className="iris-mitre-tactic-id mono">{t.tactic_id}</div>
                    <div className="iris-mitre-tactic-name">{t.name}</div>
                    {matched.length > 0 && (
                      <div className="iris-mitre-techniques">
                        {matched.map((m, i) => (
                          <Badge key={i} color="var(--status-critical)">
                            {m.technique_id} · {m.technique}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {actor && (
          <Card title="Threat actor attribution" accent={severityColor(actor.threat_level as 1 | 2 | 3 | 4 | 5)}>
            <KeyValueGrid
              cols={2}
              items={[
                { label: 'Name', value: actor.name },
                { label: 'Aliases', value: actor.aliases.join(', ') || '-' },
                { label: 'Attribution', value: actor.attribution },
                { label: 'Threat level', value: `${actor.threat_level}/5`, mono: true },
                { label: 'Active since', value: actor.active_since, mono: true },
                { label: 'Known malware', value: actor.known_malware.join(', ') },
                { label: 'Target sectors', value: actor.target_sectors.join(', ') },
                { label: 'Campaign', value: alert.campaign ?? '-', mono: true },
              ]}
            />
          </Card>
        )}

        <Card title="Recommended actions">
          {alert.recommended_actions.length === 0 ? (
            <div className="caption">No actions recommended.</div>
          ) : (
            <ul className="iris-alert-actions">
              {alert.recommended_actions.map((a, i) => (
                <li key={i}>
                  <span className="iris-alert-action-num mono">{i + 1}</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Evidence">
          {alert.evidence.length === 0 ? (
            <div className="caption">No evidence attached.</div>
          ) : (
            <ul className="iris-alert-evidence">
              {alert.evidence.map((e, i) => (
                <li
                  key={i}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    downloadJson(`iris-evidence-${alert.alert_id}-${i + 1}-${demoTimestamp()}.json`, {
                      alert_id: alert.alert_id,
                      evidence: e,
                      exported_at: new Date().toISOString(),
                      classification: 'official',
                    });
                    notify({
                      kind: 'success',
                      title: 'Evidence exported',
                      body: `${e.kind.toUpperCase()} evidence reference ${e.ref} downloaded as a chain-of-custody record.`,
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.currentTarget.click();
                    }
                  }}
                >
                  <span className="iris-alert-evidence-kind mono">[{e.kind.toUpperCase()}]</span>
                  <div>
                    <div>{e.description}</div>
                    <div className="caption mono">
                      {e.ref} · {fmtUTC(e.timestamp)}
                    </div>
                  </div>
                  <ExternalLink size={12} strokeWidth={1.5} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Open in scenarios" subtitle="Project the impact of this alert across the dependency graph">
          <Button
            variant="primary"
            icon={<Workflow size={14} strokeWidth={1.5} />}
            onClick={() =>
              navigate(`/scenarios?seed=${encodeURIComponent(alert.asset_ids.join(','))}`)
            }
          >
            Run cascade simulation
          </Button>
        </Card>
      </div>
    </div>
  );
}
